import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { generateId } from '../../utils/generateId';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
  Chip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Add as AddIcon,
  UploadFile as ImportIcon,
  AutoFixHigh as AutoIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  DownloadOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { workshopService } from '../../services/workshop.service';
import { sessionService } from '../../services/session.service';
import { Workshop } from '../../types/workshop.types';
import { AddStudentInput } from '../../types/session.types';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../hooks/useAuth';

// Intentionally limited to primary workshop delivery regions
const timezoneOptions = [
  { value: 'UTC', label: 'UTC' },
  { value: 'EST', label: 'EST (Eastern)' },
  { value: 'PST', label: 'PST (Pacific)' },
  { value: 'GMT', label: 'GMT' },
  { value: 'IST', label: 'IST (India)' },
];

const DEFAULT_DURATION = 90;
const DEFAULT_MAX_STUDENTS = 20;

interface StudentCard {
  id: string;
  name: string;
  email: string;
  tenantUrl: string;
  tenantAdminEmail: string;
  tenantAdminPassword: string;
  sharingPortalUrl: string;
  skytapPortalPassword: string;
}

const createStudent = (overrides?: Partial<Omit<StudentCard, 'id'>>): StudentCard => ({
  id: generateId(),
  name: '',
  email: '',
  tenantUrl: '',
  tenantAdminEmail: '',
  tenantAdminPassword: '',
  sharingPortalUrl: '',
  skytapPortalPassword: '',
  ...overrides,
});

interface InstructorOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const CreateSession: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();

  // Wizard step
  const [wizardStep, setWizardStep] = useState(1);

  // Step 1 state
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loadingWorkshops, setLoadingWorkshops] = useState(true);
  const [selectedWorkshopId, setSelectedWorkshopId] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [duration, setDuration] = useState<number | string>(DEFAULT_DURATION);
  const [maxStudents, setMaxStudents] = useState<number | string>(DEFAULT_MAX_STUDENTS);
  const [timezone, setTimezone] = useState('UTC');
  const [enableProgressTracking, setEnableProgressTracking] = useState(true);
  const [allowHelpRequests, setAllowHelpRequests] = useState(true);
  const [sendReminders, setSendReminders] = useState(true);
  const [recordSession, setRecordSession] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Co-instructor state
  const [instructors, setInstructors] = useState<InstructorOption[]>([]);
  const [selectedCoInstructors, setSelectedCoInstructors] = useState<InstructorOption[]>([]);
  const [loadingInstructors, setLoadingInstructors] = useState(false);

  // Step 2 state
  const [students, setStudents] = useState<StudentCard[]>([]);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [creating, setCreating] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const loadWorkshops = useCallback(async () => {
    setLoadingWorkshops(true);
    try {
      const res = await workshopService.getWorkshops({ status: 'published', limit: 50 });
      setWorkshops(res.data);
    } catch {
      showNotification('Failed to load tech evals', 'error');
    } finally {
      setLoadingWorkshops(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadWorkshops();
    // Load instructors for co-instructor selection
    setLoadingInstructors(true);
    sessionService.getInstructors()
      .then((list) => setInstructors(list.filter((i) => i.id !== user?.id)))
      .catch(() => {})
      .finally(() => setLoadingInstructors(false));
  }, [loadWorkshops, user?.id]);

  const handleNumberChange = (setter: (v: number | string) => void, value: string) => {
    if (value === '') {
      setter('');
      return;
    }
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed)) {
      setter(parsed);
    }
  };

  const canProceedStep1 = selectedWorkshopId && sessionName.trim();

  const goToStep2 = () => {
    if (!canProceedStep1) {
      setTouched({ sessionName: true });
      showNotification('Select a tech eval and enter a session name', 'error');
      return;
    }
    setWizardStep(2);
  };

  const addManualStudent = () => {
    setStudents((prev) => [...prev, createStudent()]);
  };

  const updateStudent = (index: number, field: keyof StudentCard, value: string) => {
    setStudents((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );
  };

  const removeStudent = (index: number) => {
    setStudents((prev) => prev.filter((_, i) => i !== index));
  };

  const duplicateStudent = (index: number) => {
    const s = students[index];
    setStudents((prev) => [...prev.slice(0, index + 1), createStudent(), ...prev.slice(index + 1)]);
  };

  const handleDownloadSampleCsv = () => {
    const header = 'Name,Email,TenantURL,TenantAdminEmail,TenantAdminPassword,SharingPortalURL,SkytapPassword';
    const row1 = 'John Doe,john@example.com,https://abc1234.id.cyberark.cloud,admin@abc1234.id.cyberark.cloud,AdminPass123!,https://sharing.example.com/portal,PortalPass123!';
    const row2 = 'Jane Smith,jane@example.com,https://abc1234.id.cyberark.cloud,admin@abc1234.id.cyberark.cloud,AdminPass123!,https://sharing.example.com/portal,PortalPass456!';
    const csv = [header, row1, row2].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-students.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvImport = () => {
    const lines = csvText.trim().split('\n').filter(Boolean);

    // Skip header row if the first field looks like a column name
    if (lines.length > 0) {
      const firstField = lines[0].split(',')[0].trim().toLowerCase();
      if (firstField === 'name' || firstField === 'email' || firstField === 'student name') {
        lines.shift();
      }
    }

    const parsed: StudentCard[] = [];
    for (const line of lines) {
      const parts = line.split(',').map((p) => p.trim());
      if (parts.length >= 2) {
        parsed.push(createStudent({
          name: parts[0] || '',
          email: parts[1] || '',
          tenantUrl: parts[2] || '',
          tenantAdminEmail: parts[3] || '',
          tenantAdminPassword: parts[4] || '',
          sharingPortalUrl: parts[5] || '',
          skytapPortalPassword: parts[6] || '',
        }));
      }
    }
    if (parsed.length === 0) {
      showNotification('No valid rows found. Format: Name,Email[,TenantURL,TenantAdminEmail,TenantAdminPassword,SharingPortalURL,SkytapPassword]', 'error');
      return;
    }
    setStudents((prev) => [...prev, ...parsed]);
    setCsvDialogOpen(false);
    setCsvText('');
    showNotification(`Imported ${parsed.length} student(s)`, 'success');
  };

  // FM2: Memoize duplicate email detection
  const duplicateIndices = useMemo(() => {
    const dupes = new Set<number>();
    const seen = new Map<string, number>();
    students.forEach((s, i) => {
      const email = s.email.trim().toLowerCase();
      if (!email) return;
      if (seen.has(email)) {
        dupes.add(seen.get(email)!);
        dupes.add(i);
      } else {
        seen.set(email, i);
      }
    });
    return dupes;
  }, [students]);

  const handleCreate = async () => {
    setSubmitAttempted(true);

    // Validate student cards if any exist
    if (students.length > 0) {
      const hasEmpty = students.some((s) => !s.name.trim() || !s.email.trim());
      if (hasEmpty) {
        showNotification('Please fill in all student names and emails', 'error');
        return;
      }
      if (duplicateIndices.size > 0) {
        showNotification('Remove duplicate email addresses before creating the session', 'error');
        return;
      }
    }

    setCreating(true);
    try {
      let scheduledStart: string | undefined;
      if (scheduledDate && scheduledTime) {
        scheduledStart = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      } else if (scheduledDate) {
        scheduledStart = new Date(`${scheduledDate}T09:00`).toISOString();
      }

      const session = await sessionService.createSession({
        workshopId: selectedWorkshopId,
        name: sessionName,
        scheduledStart,
        duration: Number(duration) || DEFAULT_DURATION,
        maxStudents: Number(maxStudents) || DEFAULT_MAX_STUDENTS,
        timezone,
        enableProgressTracking,
        allowHelpRequests,
        sendReminders,
        recordSession,
      });

      // Add co-instructors if any selected
      if (selectedCoInstructors.length > 0) {
        try {
          await sessionService.addCoInstructors(session.id, selectedCoInstructors.map((i) => i.id));
        } catch {
          showNotification('Session created but failed to add co-instructors', 'warning');
        }
      }

      // Add students if any
      if (students.length > 0) {
        const validStudents: AddStudentInput[] = students.map((s) => ({
          name: s.name,
          email: s.email,
          tenantUrl: s.tenantUrl || undefined,
          tenantAdminEmail: s.tenantAdminEmail || undefined,
          tenantAdminPassword: s.tenantAdminPassword || undefined,
          sharingPortalUrl: s.sharingPortalUrl || undefined,
          skytapPortalPassword: s.skytapPortalPassword || undefined,
        }));

        await sessionService.addStudents(session.id, validStudents);

        // Send invitation emails after students are enrolled
        try {
          await sessionService.sendInvitations(session.id);
        } catch {
          // Non-blocking — session is created, invitations failed
          showNotification('Session created but failed to send invitation emails', 'warning');
          navigate(`/instructor/session/${session.id}`);
          return;
        }
      }

      showNotification('Session created!', 'success');
      navigate(`/instructor/session/${session.id}`);
    } catch {
      showNotification('Failed to create session', 'error');
    } finally {
      setCreating(false);
    }
  };

  // --- Step 1: Session Details ---
  const renderStep1 = () => (
    <Paper sx={{ maxWidth: 900, mx: 'auto', p: '40px' }}>
      {/* Select Workshop */}
      <Typography variant="h6" sx={{ mb: 1 }}>Select Tech Eval</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose a published tech eval for this session
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {loadingWorkshops ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : workshops.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No published tech evals available. Publish a tech eval first.
        </Alert>
      ) : (
        <Box sx={{ mb: 4 }}>
          <Autocomplete
            options={workshops}
            getOptionLabel={(ws) => ws.title}
            value={workshops.find((ws) => ws.id === selectedWorkshopId) || null}
            onChange={(_, ws) => setSelectedWorkshopId(ws?.id || '')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="Search tech evals..." variant="outlined" />
            )}
            renderOption={(props, ws) => (
              <li {...props} key={ws.id}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{ws.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ws.stepCount ?? 0} steps · {ws.estimatedDuration} min · {ws.difficulty}
                  </Typography>
                </Box>
              </li>
            )}
            sx={{ mb: 2 }}
          />
          {selectedWorkshopId && (() => {
            const ws = workshops.find((w) => w.id === selectedWorkshopId);
            if (!ws) return null;
            return (
              <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'primary.light', backgroundColor: 'action.hover' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{ws.title}</Typography>
                {ws.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {ws.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`${ws.stepCount ?? 0} steps`} size="small" variant="outlined" />
                  <Chip label={`${ws.estimatedDuration} min`} size="small" variant="outlined" />
                  <Chip label={ws.difficulty} size="small" variant="outlined" />
                </Box>
              </Paper>
            );
          })()}
        </Box>
      )}

      {/* Session Details */}
      <Typography variant="h6" sx={{ mb: 1 }}>Session Details</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure session name, schedule, and capacity
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <TextField
        fullWidth
        label="Session Name"
        required
        placeholder="e.g., March 2026 Training - Morning Session"
        value={sessionName}
        onChange={(e) => setSessionName(e.target.value)}
        onBlur={() => setTouched((prev) => ({ ...prev, sessionName: true }))}
        error={touched.sessionName && !sessionName.trim()}
        helperText={touched.sessionName && !sessionName.trim() ? 'Session name is required' : ''}
        sx={{ mb: 3 }}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Scheduled Date"
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Scheduled Time"
            type="time"
            value={scheduledTime}
            onChange={(e) => setScheduledTime(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText={scheduledDate && !scheduledTime ? 'Defaults to 09:00 AM' : ''}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            label="Duration (minutes)"
            type="number"
            value={duration}
            onChange={(e) => handleNumberChange(setDuration, e.target.value)}
            inputProps={{ min: 15, max: 480 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            label="Max Students"
            type="number"
            value={maxStudents}
            onChange={(e) => handleNumberChange(setMaxStudents, e.target.value)}
            inputProps={{ min: 1, max: 100 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            select
            label="Time Zone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            {timezoneOptions.map((tz) => (
              <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {/* Session Settings */}
      <Typography variant="h6" sx={{ mb: 1 }}>Session Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure session behavior
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Box sx={{ mb: 1 }}>
        <FormControlLabel
          control={<Checkbox checked={enableProgressTracking} onChange={(e) => setEnableProgressTracking(e.target.checked)} />}
          label="Enable real-time progress tracking"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
          Track student progress through tech eval steps in real time
        </Typography>
      </Box>
      <Box sx={{ mb: 1 }}>
        <FormControlLabel
          control={<Checkbox checked={allowHelpRequests} onChange={(e) => setAllowHelpRequests(e.target.checked)} />}
          label="Allow help requests"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
          Students can request instructor assistance during the session
        </Typography>
      </Box>
      <Box sx={{ mb: 1 }}>
        <FormControlLabel
          control={<Checkbox checked={sendReminders} onChange={(e) => setSendReminders(e.target.checked)} />}
          label="Send automated reminders"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
          Send email reminders before the session starts
        </Typography>
      </Box>
      <Box sx={{ mb: 4 }}>
        <FormControlLabel
          control={<Checkbox checked={recordSession} onChange={(e) => setRecordSession(e.target.checked)} />}
          label="Record session for later review"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
          Capture session activity for post-session analysis
        </Typography>
      </Box>

      {/* Invite Co-Instructors */}
      <Typography variant="h6" sx={{ mb: 1 }}>Invite Co-Instructors (Optional)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Co-instructors will have full access to monitor the session, respond to help requests, and manage students.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Autocomplete
        multiple
        options={instructors}
        value={selectedCoInstructors}
        onChange={(_, newValue) => setSelectedCoInstructors(newValue)}
        getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={loadingInstructors}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search instructors"
            placeholder="Type to search..."
            variant="outlined"
            size="small"
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={`${option.firstName} ${option.lastName}`}
              size="small"
              color="primary"
            />
          ))
        }
        sx={{ mb: 4 }}
      />

      {/* Info Box */}
      <Alert severity="info" sx={{ mb: 4, borderLeft: (theme: any) => `4px solid ${theme.palette.primary.main}` }}>
        <strong>Next Step: Assign Students</strong> — After configuring session details, you'll assign students and provide environment credentials.
      </Alert>

      {/* Actions */}
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/instructor/sessions')}>
          Cancel
        </Button>
        <Button
          variant="contained"
          endIcon={<NextIcon />}
          onClick={goToStep2}
          disabled={!canProceedStep1}
        >
          Continue to Student Assignment
        </Button>
      </Box>
    </Paper>
  );

  // --- Step 2: Student Assignment ---
  const renderStep2 = () => (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Add Methods */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={0}
            onClick={addManualStudent}
            sx={{
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: 'divider',
              '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
            }}
          >
            <AddIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Add Manually</Typography>
            <Typography variant="caption" color="text.secondary">Add students one at a time</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={0}
            onClick={() => setCsvDialogOpen(true)}
            sx={{
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: 'divider',
              '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
            }}
          >
            <ImportIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Bulk Import</Typography>
            <Typography variant="caption" color="text.secondary">Import from CSV</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={0}
            onClick={() => showNotification('Auto-generate coming soon', 'info')}
            sx={{
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: 'divider',
              opacity: 0.6,
              '&:hover': { borderColor: 'divider' },
            }}
          >
            <AutoIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Auto-Generate</Typography>
            <Typography variant="caption" color="text.secondary">Coming soon</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Session Summary Card */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: (theme: any) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
        }}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{students.length}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>Students Added</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{Number(maxStudents) || DEFAULT_MAX_STUDENTS}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>Max Capacity</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{Number(duration) || DEFAULT_DURATION}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>Minutes</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {scheduledDate || '—'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>Session Date</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Student List */}
      {students.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No students added yet. Use the options above to add students.
          </Typography>
        </Paper>
      ) : (
        students.map((student, index) => (
          <Paper key={student.id} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Chip label={`Student ${index + 1}`} size="small" />
              <Box>
                <IconButton size="small" onClick={() => duplicateStudent(index)}>
                  <DuplicateIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => removeStudent(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Full Name"
                  value={student.name}
                  onChange={(e) => updateStudent(index, 'name', e.target.value)}
                  error={submitAttempted && !student.name.trim()}
                  helperText={submitAttempted && !student.name.trim() ? 'Name is required' : ''}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  value={student.email}
                  onChange={(e) => updateStudent(index, 'email', e.target.value)}
                  error={(submitAttempted && !student.email.trim()) || duplicateIndices.has(index)}
                  helperText={
                    duplicateIndices.has(index)
                      ? 'Duplicate email — this student is already in the list'
                      : submitAttempted && !student.email.trim()
                      ? 'Email is required'
                      : ''
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, mb: 0.5, display: 'block' }}>
                  Environment Credentials (Optional)
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tenant URL"
                  value={student.tenantUrl}
                  onChange={(e) => updateStudent(index, 'tenantUrl', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tenant Admin Email"
                  value={student.tenantAdminEmail}
                  onChange={(e) => updateStudent(index, 'tenantAdminEmail', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tenant Admin Password"
                  value={student.tenantAdminPassword}
                  onChange={(e) => updateStudent(index, 'tenantAdminPassword', e.target.value)}
                  slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Sharing Portal URL"
                  value={student.sharingPortalUrl}
                  onChange={(e) => updateStudent(index, 'sharingPortalUrl', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Skytap Portal Password"
                  value={student.skytapPortalPassword}
                  onChange={(e) => updateStudent(index, 'skytapPortalPassword', e.target.value)}
                  slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
                />
              </Grid>
            </Grid>
          </Paper>
        ))
      )}

      {/* Actions */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button startIcon={<BackIcon />} onClick={() => setWizardStep(1)}>
          Previous Step
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/instructor/sessions')}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? <CircularProgress size={24} color="inherit" /> : 'Create Session & Send Invites'}
          </Button>
        </Box>
      </Box>

      {/* CSV Import Dialog */}
      <Dialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Import Students (CSV)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste CSV data below. Format: <strong>Name,Email,TenantURL,TenantAdminEmail,TenantAdminPassword,SharingPortalURL,SkytapPassword</strong> (one per line).
            Only Name and Email are required — all other columns are optional. Username and password are auto-generated.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadOutlined />}
            onClick={handleDownloadSampleCsv}
            sx={{ mb: 2, textTransform: 'none' }}
          >
            Download Sample CSV
          </Button>
          <TextField
            fullWidth
            multiline
            minRows={8}
            placeholder={`John Doe,john@example.com\nJane Smith,jane@example.com`}
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCsvImport} disabled={!csvText.trim()}>
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => wizardStep === 1 ? navigate('/instructor/sessions') : setWizardStep(1)}
            size="small"
          >
            {wizardStep === 1 ? 'Cancel' : 'Back'}
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {wizardStep === 1 ? 'Create New Session' : 'Assign Students to Session'}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Step {wizardStep} of 2
        </Typography>
      </Box>

      {/* Progress Indicator */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4, gap: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: wizardStep >= 1 ? 'primary.main' : 'grey.300',
            color: 'white',
            fontWeight: 'bold',
            fontSize: 14,
          }}
        >
          {wizardStep > 1 ? <CheckIcon sx={{ fontSize: 18 }} /> : '1'}
        </Box>
        <Box sx={{ width: 80, height: 2, backgroundColor: wizardStep >= 2 ? 'primary.main' : 'grey.300' }} />
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: wizardStep >= 2 ? 'primary.main' : 'grey.300',
            color: 'white',
            fontWeight: 'bold',
            fontSize: 14,
          }}
        >
          2
        </Box>
      </Box>

      {wizardStep === 1 ? renderStep1() : renderStep2()}
    </Box>
  );
};

export default CreateSession;
